import { InboxreaderPurchase } from "../purchase/inboxreader-purchase";
import { v4 } from "uuid";
import { Purchase } from "@/models/base";
import { IInboxReaderFetchOptions, InboxreaderService, InboxReaderType } from "@/services/inboxreader-service";

const LIMIT = 20;

export abstract class InboxTypeState {
	public selection: InboxreaderPurchase[] = [];
	public purchases: InboxreaderPurchase[] = [];
	// anchor is the entry of the last plain click; a shift click selects
	// everything between the anchor and the clicked entry.
	private anchor:InboxreaderPurchase | null = null;
	protected archive:boolean = false;
	public unprocessedCount:number = 0;
	public totalRecords:number = 0;
	private fetchThread:string = "";
	private lastRequestHadNoResult:boolean = false;
	protected vue:Vue;

	public constructor(vue:Vue){
		this.vue = vue;
	}

	public endOfDataReached():boolean {
		return this.lastRequestHadNoResult;
	}

	public clearSelection(){
		this.selection.splice(0, this.selection.length);
		this.anchor = null;
	}

	protected entriesMatch(a:InboxreaderPurchase, b:InboxreaderPurchase):boolean{
		return a.Files.some(f=>b.Files.some(pf=>pf.ID == f.ID));
	}

	public isSelected(item:InboxreaderPurchase):boolean{
		return this.selection.some(p=>this.entriesMatch(p, item));
	}

	public selectItem(item:InboxreaderPurchase){
		let i = this.selection.findIndex(p=>this.entriesMatch(p, item));
		if (i == -1) {
			this.selection.push(item);
		} else {
			this.selection.splice(i, 1);
		}
		this.anchor = item;
	}

	// orderedEntries lists every selectable entry in the order the list shows
	// them, so a range can be taken out of it.
	protected orderedEntries():InboxreaderPurchase[]{
		return this.purchases;
	}

	// selectRange adds everything between the anchor and the given entry to the
	// selection. Without an anchor in the list it behaves as a plain click.
	public selectRange(item:InboxreaderPurchase){
		let entries = this.orderedEntries();
		let from = this.anchor ? entries.findIndex(p=>this.entriesMatch(p, this.anchor!)) : -1;
		let to = entries.findIndex(p=>this.entriesMatch(p, item));
		if (from == -1 || to == -1) {
			this.selectItem(item);
			return;
		}
		let [start, end] = from < to ? [from, to] : [to, from];
		for (let i = start; i <= end; i++) {
			if (!this.isSelected(entries[i])) {
				this.selection.push(entries[i]);
			}
		}
	}

	// getInboxTypes tells the server which kind of documents this state shows.
	// An empty list means no filter at all: every document of the dossier.
	protected abstract getInboxTypes():InboxReaderType[];

	protected getFetchOptions(processed:boolean, search:string, labelIds:number[]):IInboxReaderFetchOptions{
		let opts:IInboxReaderFetchOptions = {processed, limit: LIMIT};
		let types = this.getInboxTypes();
		if (types.length) opts.types = types;
		if (search) opts.search = search;
		if (labelIds.length) opts.labels = labelIds;
		return opts;
	}


	async fetch(processed:boolean, search:string = "", labelIds:number[] = []){
		let threadId = v4();
		this.fetchThread = threadId;
		this.archive = processed;
		try{
			let result = await InboxreaderService.getInboxreaderPurchases(this.getFetchOptions(processed, search, labelIds));
			if (this.fetchThread != threadId) return;
			this.lastRequestHadNoResult = false;
			this.purchases = result.data;
			this.totalRecords = result.records;
			if (!processed && !search) {
				this.unprocessedCount = result.records;
			}
			this.clearSelection();
		}catch(err){
			console.error(err);
		}finally{
			if (this.fetchThread != threadId) return;
			this.fetchThread = "";
		}
	}

	private removeAlreadyFetchedRecords(items:InboxreaderPurchase[]){
		for (let i = items.length-1;i>=0;i--) {
			let c = this.purchases.findIndex(p=>p.ID == items[i].ID);
			if (c != -1) {
				items.splice(c,1);
			}
		}
	}

	async fetchMore(processed:boolean, search:string = "", labelIds:number[] = []){
		if (this.fetchThread != "") return;
		if (this.totalRecords <= this.purchases.length) {
			this.lastRequestHadNoResult = true;
			return;
		};
		let threadId = v4();
		this.fetchThread = threadId;
		try{
			let options = this.getFetchOptions(processed, search, labelIds);
			options.offset = this.purchases.length;
			let result = await InboxreaderService.getInboxreaderPurchases(options);
			this.removeAlreadyFetchedRecords(result.data);
			if (result.data.length == 0) {
				this.lastRequestHadNoResult = true;
			}
			this.purchases.push(...result.data);
		}catch(err){
			console.error(err);
		}finally{
			if (this.fetchThread != threadId) return;
			this.fetchThread = "";
		}
	}

	public abstract createPurchase():Promise<Purchase>;
}